import React, { useState } from 'react'
import { api, ApiError } from '../lib/api'
import { useOverlayA11y } from '../hooks/useOverlayA11y'

const linkButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit',
}

/** Generic "confirm it's really you" modal for any MFA-gated sensitive
 * action — calls POST /mfa/reauth for a short-lived step_up_token and hands
 * it to the caller, which is responsible for attaching it as the
 * X-Step-Up-Token header on the actual action and closing this modal. Used
 * both from Account Settings (disable / regenerate recovery codes) and
 * reactively from any other page whose mutating call comes back 403 asking
 * for a fresh MFA confirmation (see AdminPayers.tsx). */
export function MfaStepUpModal({ title, description, onClose, onVerified }: {
  title: string
  description: string
  onClose: () => void
  onVerified: (stepUpToken: string) => Promise<void> | void
}) {
  const [code, setCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const modalRef = useOverlayA11y(onClose)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { step_up_token } = await api('/mfa/reauth', {
        method: 'POST',
        body: JSON.stringify(useRecoveryCode ? { recovery_code: recoveryCode } : { code }),
      })
      await onVerified(step_up_token)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <h2>{title}</h2>
        <p className="muted" style={{ fontSize: 13.5 }}>{description}</p>
        <form onSubmit={submit}>
          {!useRecoveryCode ? (
            <div className="field">
              <label>Authentication code</label>
              <input className="inp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value)} required />
            </div>
          ) : (
            <div className="field">
              <label>Recovery code</label>
              <input className="inp" autoFocus placeholder="xxxx-xxxx-xxxx" value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} required />
            </div>
          )}
          {error && <div className="form-error">{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn small outline" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn small" disabled={busy}>{busy ? 'Verifying…' : 'Confirm'}</button>
          </div>
        </form>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          <button type="button" style={linkButtonStyle} onClick={() => { setUseRecoveryCode(!useRecoveryCode); setError(null) }}>
            {useRecoveryCode ? 'Use your authenticator app instead' : 'Use a recovery code instead'}
          </button>
        </p>
      </div>
    </div>
  )
}
